/**
 * authoringFrame — WS-EXIF. The frame a view's normalized geometry was drawn on, and what to
 * do when the photo in front of the operator is a different shape.
 *
 * A print-area polygon is stored as bare 0..1 numbers. Those numbers mean nothing on their own:
 * `(0.09, 0.14)` is a point only once you know the image that produced it. That was harmless
 * while the frame never moved, and the frame moves two ways:
 *
 *  * **EXIF.** A browser applies a photo's `Orientation` when it decodes it; the engine did not
 *    (fixed in `engine/genz-engine/src/image_io.rs`). A portrait phone shot with `Orientation = 6`
 *    was 720×1484 in the browser and 1484×720 in the engine — the TRANSPOSE — so a quad authored
 *    in one frame came back rotated and oversized in the other.
 *  * **Replace reference image.** A new photo of a different shape re-frames every polygon on the
 *    view, silently.
 *
 * `WireView.base_image_size` records the authoring frame, and these helpers compare it with the
 * photo actually on the canvas. The rule the UI follows: aspects agree → draw; they do not →
 * do NOT draw the raw quad, say so, and offer a re-fit.
 */
import type { Polygon, WireView } from "@genz/engine-bind";

/** A pixel frame. */
export interface Frame {
  w: number;
  h: number;
}

/**
 * How far two aspect ratios may drift and still be "the same shape".
 *
 * 1 % — wide enough to absorb an off-by-one from a decoder or a rounded stored size (720×1484 vs
 * 721×1484 is 0.14 % out), far too narrow to let a real re-frame through: the transpose of a
 * phone photo is ~325 % out and a 4:3 → 16:9 swap is 33 %.
 */
export const ASPECT_EPSILON = 0.01;

/** The frame a view's geometry was authored on, or `null` for a record written before the
 *  field existed (and for any stored value that is not two positive numbers). */
export function authoringFrameOf(view?: WireView | null): Frame | null {
  const size = view?.base_image_size;
  if (!Array.isArray(size) || size.length !== 2) return null;
  const [w, h] = size;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { w, h };
}

/** Whether two frames have the same shape (aspect within [`ASPECT_EPSILON`]). */
export function sameShape(a: Frame, b: Frame): boolean {
  const ra = a.w / a.h;
  const rb = b.w / b.h;
  return Math.abs(ra - rb) / Math.max(ra, rb) <= ASPECT_EPSILON;
}

/** Whether `b` is `a` turned on its side — the EXIF case, where the two aspects are reciprocal. */
export function isTransposed(a: Frame, b: Frame): boolean {
  return sameShape({ w: a.h, h: a.w }, b);
}

/**
 * What the canvas should do with a view's stored geometry, given the photo now decoded for it.
 *
 *  * `draw` — the frames agree, or nothing was recorded (a legacy view: treat the current photo
 *    as the frame, exactly as the editor always did, and stamp the size on the next Save).
 *  * `transpose` — the photo is the authoring frame on its side. A re-fit is pure arithmetic.
 *  * `redetect` — a different shape entirely. Only detection can recover the print area.
 */
export type FrameVerdict = "draw" | "transpose" | "redetect";

export function frameVerdict(authored: Frame | null, current: Frame | null): FrameVerdict {
  if (!authored || !current) return "draw";
  if (sameShape(authored, current)) return "draw";
  return isTransposed(authored, current) ? "transpose" : "redetect";
}

/**
 * Turn normalized geometry a quarter-turn: `(x, y) → (1 − y, x)`.
 *
 * This undoes the frame error an `Orientation = 6` photo produced — the common one, a phone
 * held upright. The engine read that portrait photo as landscape, so a point the operator
 * placed at `(x, y)` of the portrait frame was recorded at the landscape frame's `(y, 1 − x)`;
 * turning back is `(1 − y, x)`.
 */
export function transposePolygon(p: Polygon): Polygon {
  return p.map((pt) => ({ x: 1 - pt.y, y: pt.x }));
}

/**
 * The OTHER quarter-turn: `(x, y) → (y, 1 − x)`.
 *
 * Reciprocal aspects do not say WHICH way the picture was turned. `Orientation = 6` (rotate 90°
 * clockwise) and `Orientation = 8` (rotate 90° anticlockwise) both produce a landscape file for
 * a portrait picture, and the frames alone cannot tell them apart — only the pixels can, and by
 * the time a template is reopened the original tag is long gone. So the editor offers its best
 * guess (the `6` case, which is what a phone held upright writes) and this, the other way.
 */
export function transposePolygonBack(p: Polygon): Polygon {
  return p.map((pt) => ({ x: pt.y, y: 1 - pt.x }));
}

/**
 * Pin the frame a view's stored geometry belongs to, just BEFORE its reference photo is swapped.
 *
 * Replacing the reference image re-frames every polygon on the view at once, and for a LEGACY
 * record there is nothing to compare the new photo against — so the swap would draw the old quad
 * on the new photo in silence, which is the failure this whole rule exists to stop. At that exact
 * moment the editor does know the outgoing photo's size, because it decoded it. Write it down.
 *
 * Only for a view that carries no frame yet: a recorded frame is already the right answer and
 * must not be overwritten by whatever happens to be on the canvas. `frame` is null when the photo
 * never decoded, and then nothing is pinned rather than a guess.
 */
export function pinOutgoingFrame(drafts: WireView[], view: string, frame: Frame | null): WireView[] {
  if (!frame) return drafts;
  return drafts.map((d) =>
    d.view_type === view && !authoringFrameOf(d)
      ? { ...d, base_image_size: [frame.w, frame.h] as [number, number] }
      : d,
  );
}

/**
 * The pixel size a browser decodes `url` to — the one helper here that touches the DOM.
 *
 * The editor already knows this for the view ON the canvas. It does not for a SIBLING view, and
 * `applyAsReference` can swap a sibling's photo: that view's masks then sit on numbers from a
 * frame nobody recorded. Measuring the outgoing photo is the only truthful way to write it down.
 *
 * Resolves to `null` rather than rejecting — for no URL, no DOM (tests, SSR), or a photo that
 * fails to load. A guess would be worse than no frame at all.
 */
export function measureFrame(url: string | null | undefined): Promise<Frame | null> {
  if (!url || typeof Image === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve(img.naturalWidth > 0 && img.naturalHeight > 0
        ? { w: img.naturalWidth, h: img.naturalHeight }
        : null);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** The operator-facing sentence for a view whose geometry does not belong to the photo on the
 *  canvas. Deliberately says nothing about EXIF: the operator did not choose the orientation
 *  tag and cannot act on it. */
export const FRAME_MISMATCH_MESSAGE =
  "This template's print area was drawn on a photo of a different shape";

/** The second line — what each button will do, which differs by verdict. */
export function frameMismatchDetail(verdict: FrameVerdict): string {
  return verdict === "transpose"
    ? "The photo is the same picture on its side. Re-fit turns the print area to match it; "
      + "if it lands the wrong way up, turn it the other way."
    : "Re-fit detects the print area again on this photo.";
}
